// Widget analytics — lightweight funnel + visit tracking for the embed widget.
//
// Context: during form-fill the visitor is NOT authenticated (guest sign-in only
// happens at the captcha/files step). Firestore rules are currently allow-all, so
// we write analytics events directly via the SDK with no auth and no backend call.
// Everything here is best-effort and wrapped in try/catch: analytics must NEVER
// break the devis form.
//
// Two collections:
//  - widget_daily  : one doc per org per day, counters (views/opens/submits). Cheap
//                    aggregate for the "visites" time series.
//  - widget_session: one doc per opened widget, tracks the furthest step reached so
//                    the dashboard can show where people abandon the form.

import { doc, setDoc, increment, serverTimestamp } from "firebase/firestore";
import { db } from "../config/FirebaseConfig";

// Ordered funnel steps. `key` matches the `step` state used in NewInvoiceClient
// (plus the synthetic "opened" / "submitted" bookends). The index is the funnel
// position. Keep in sync with the dashboard in testing_react.
export const WIDGET_STEPS = [
  { key: "opened", label: "Ouvert" },
  { key: "form", label: "Questionnaire" },
  { key: "infos-1", label: "Coordonnées" },
  { key: "infos-2", label: "Adresses" },
  { key: "infos-3", label: "Audit" },
  { key: "files", label: "Pièces & validation" },
  { key: "submitted", label: "Devis généré" },
];

export const widgetStepIndex = (key) =>
  WIDGET_STEPS.findIndex((s) => s.key === key);

function genId() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch (e) {
    // ignore — fall through to the random fallback
  }
  return (
    "s_" + Math.random().toString(36).slice(2) + Date.now().toString(36)
  );
}

function todayKey() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

async function bumpDaily(master_id, field) {
  if (!master_id) return;
  try {
    const date = todayKey();
    await setDoc(
      doc(db, "widget_daily", `${master_id}_${date}`),
      {
        master_id,
        date,
        [field]: increment(1),
        updated_at: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (e) {
    console.warn("widget analytics (daily) failed", e);
  }
}

// Count one unique visit per browser session per org (a "visite"). Guarded by
// sessionStorage so navigating between pages of the host site doesn't inflate it.
export async function trackView(master_id) {
  if (!master_id) return;
  try {
    const flag = `hex_wv_${master_id}`;
    if (typeof sessionStorage !== "undefined") {
      if (sessionStorage.getItem(flag)) return;
      sessionStorage.setItem(flag, "1");
    }
    await bumpDaily(master_id, "views");
  } catch (e) {
    console.warn("widget analytics (view) failed", e);
  }
}

// Called when the visitor opens the widget. Creates the session doc and returns
// its id (null on failure — callers must tolerate a missing session id).
export async function startWidgetSession(master_id, { device, referrer } = {}) {
  if (!master_id) return null;
  const session_id = genId();
  try {
    await setDoc(doc(db, "widget_session", session_id), {
      session_id,
      master_id,
      device: device || "desktop",
      referrer: referrer || "",
      created_at: serverTimestamp(),
      updated_at: serverTimestamp(),
      last_step: "opened",
      max_step_index: 0,
      reached: { opened: serverTimestamp() },
      submitted: false,
      devis_id: null,
    });
    bumpDaily(master_id, "opens");
    return session_id;
  } catch (e) {
    console.warn("widget analytics (session start) failed", e);
    return null;
  }
}

// Record a step transition. `maxStepIndex` is the furthest step reached so far
// (computed by the caller — Firestore has no atomic max), used by the funnel.
export async function trackWidgetStep(master_id, session_id, step, maxStepIndex) {
  if (!session_id) return;
  if (widgetStepIndex(step) < 0) return; // transient/unknown step (e.g. "invoice")
  try {
    await setDoc(
      doc(db, "widget_session", session_id),
      {
        last_step: step,
        max_step_index: maxStepIndex,
        updated_at: serverTimestamp(),
        // Nested merge preserves previously reached steps.
        reached: { [step]: serverTimestamp() },
      },
      { merge: true }
    );
  } catch (e) {
    console.warn("widget analytics (step) failed", e);
  }
}

// Record the prospect's contact details as soon as they enter them (before any
// authentication / submission), so masters can follow up with people who left
// their email/phone but didn't finish the form. `has_contact` flags the session
// for easy dashboard filtering. Best-effort — must never break the form.
export async function trackWidgetContact(master_id, session_id, contact = {}) {
  if (!session_id) return;
  try {
    const clean = (v) => (v == null ? "" : String(v).trim());
    await setDoc(
      doc(db, "widget_session", session_id),
      {
        has_contact: true,
        contact: {
          name: clean(contact.name),
          first_name: clean(contact.first_name),
          email: clean(contact.email),
          phone: clean(contact.phone),
          city: clean(contact.city),
        },
        contact_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (e) {
    console.warn("widget analytics (contact) failed", e);
  }
}

// Record a successful devis submission.
export async function trackWidgetSubmit(master_id, session_id, devis_id, extra = {}) {
  try {
    if (session_id) {
      await setDoc(
        doc(db, "widget_session", session_id),
        {
          submitted: true,
          devis_id: devis_id || null,
          last_step: "submitted",
          max_step_index: WIDGET_STEPS.length - 1,
          updated_at: serverTimestamp(),
          submitted_at: serverTimestamp(),
          reached: { submitted: serverTimestamp() },
          ...extra,
        },
        { merge: true }
      );
    }
    bumpDaily(master_id, "submits");
  } catch (e) {
    console.warn("widget analytics (submit) failed", e);
  }
}
